import React, { useEffect, useMemo, useState } from 'react';
import {
  Stack, Group, Title, Text, Paper, Badge, Table, Loader, ScrollArea, Select, Pagination, SimpleGrid,
} from '@mantine/core';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useAuthenticatedData from '../../../hooks/useAuthenticatedData';
import config from '../../../config';
import DraftBadge from '../../common/DraftBadge';

const COVERAGE_COLORS = { have_all: 'green', have_some: 'yellow', have_none: 'red', unknown: 'gray' };
const ACTION_COLORS = {
  quote_now: 'green', quote_with_review: 'teal', hold_for_sourcing: 'yellow',
  request_more_info: 'blue', deprioritize: 'gray',
};
const PAGE_SIZE = 50;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' }) : '—');
const isOverdue = (iso) => (iso ? new Date(iso) < new Date() : false);
const num = (v) => (v == null ? '—' : Number(v).toLocaleString());

export default function MyDeals() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [sp, setSp] = useState(params.get('sp') || '');
  const [page, setPage] = useState(1);

  const { data: people } = useAuthenticatedData(`${config.apiBaseUrl}/api/rotabull/salespeople`);
  const options = useMemo(
    () => (people || []).map((p) => ({ value: p.ccode, label: `${p.name} (${num(p.dealCount)})` })),
    [people],
  );

  // Default to the largest real rep once the list loads, if nothing preselected.
  useEffect(() => {
    if (!sp && people?.length) {
      const firstReal = people.find((p) => p.ccode !== '__unassigned__' && p.ccode !== 'ITS') || people[0];
      if (firstReal) setSp(firstReal.ccode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [people]);

  const url = useMemo(() => {
    if (!sp) return null;
    const p = new URLSearchParams({
      salesperson: sp, page: String(page - 1), pageSize: String(PAGE_SIZE),
      sortField: 'rfq_create_date', sortDir: 'desc',
    });
    return `${config.apiBaseUrl}/api/vesper/worklist?${p.toString()}`;
  }, [sp, page]);
  const { data, isLoading } = useAuthenticatedData(url);

  const rows = data?.rows || [];
  const total = data?.total || 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const selected = (people || []).find((p) => p.ccode === sp);

  const onSelect = (v) => { setSp(v || ''); setPage(1); setParams(v ? { sp: v } : {}); };

  return (
    <Stack gap="md" p="md">
      <DraftBadge />
      <div>
        <Title order={2}>My Deals</Title>
        <Text size="sm" c="dimmed">A salesperson's inbound deal stream. (Pick a rep below — this will default to the logged-in user once user↔CCODE mapping is populated.)</Text>
      </div>

      <Group>
        <Select
          label="Salesperson" w={320} searchable
          data={options} value={sp} onChange={onSelect}
          placeholder="Select a salesperson"
        />
      </Group>

      {sp && (
        <SimpleGrid cols={{ base: 2, sm: 4 }}>
          <Kpi label="Salesperson" value={selected?.name || sp} />
          <Kpi label="Total deals" value={num(selected?.dealCount)} />
          <Kpi label="On this page" value={num(rows.length)} />
          <Kpi label="Status" value={selected?.active === false ? 'Inactive' : 'Active'} />
        </SimpleGrid>
      )}

      <Paper withBorder radius="md">
        <ScrollArea>
          <Table fz="sm" striped highlightOnHover miw={980}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Score</Table.Th><Table.Th>Customer</Table.Th><Table.Th>Segment</Table.Th>
                <Table.Th ta="right">Lines</Table.Th><Table.Th>Action</Table.Th><Table.Th>Coverage</Table.Th>
                <Table.Th>Due</Table.Th><Table.Th>Received</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {isLoading && <Table.Tr><Table.Td colSpan={8}><Group justify="center" p="md"><Loader size="sm" /></Group></Table.Td></Table.Tr>}
              {!isLoading && rows.map((o) => (
                <Table.Tr key={o.deal_public_id} style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/sales/cockpit?start=${encodeURIComponent(o.deal_public_id)}`)}>
                  <Table.Td>{o.priority_score != null ? <Badge color="indigo" variant="filled">{o.priority_score}</Badge> : <Text c="dimmed" size="xs">—</Text>}</Table.Td>
                  <Table.Td>
                    <Text size="sm">{o.buyer_company_name || o.customer_name_dw || '—'}</Text>
                    {(o.IsAirframeKeyCustomer === 'Y' || o.IsEngineKeyCustomer === 'Y') && <Badge size="xs" color="grape" variant="light">key</Badge>}
                  </Table.Td>
                  <Table.Td><Text size="xs" c="dimmed">{o.MarketSegment || '—'}</Text></Table.Td>
                  <Table.Td ta="right">{o.line_count ?? '—'}</Table.Td>
                  <Table.Td>{o.recommended_action ? <Badge size="sm" variant="light" color={ACTION_COLORS[o.recommended_action] || 'gray'}>{o.recommended_action.replace(/_/g, ' ')}</Badge> : <Text size="xs" c="dimmed">not enriched</Text>}</Table.Td>
                  <Table.Td>{o.inventory_coverage ? <Badge size="sm" variant="dot" color={COVERAGE_COLORS[o.inventory_coverage] || 'gray'}>{o.inventory_coverage.replace(/_/g, ' ')}</Badge> : '—'}</Table.Td>
                  <Table.Td><Text size="xs" c={isOverdue(o.rfq_response_due_by) ? 'red' : undefined}>{fmtDate(o.rfq_response_due_by)}</Text></Table.Td>
                  <Table.Td><Text size="xs" c="dimmed">{fmtDate(o.rfq_create_date)}</Text></Table.Td>
                </Table.Tr>
              ))}
              {!isLoading && sp && rows.length === 0 && <Table.Tr><Table.Td colSpan={8}><Text c="dimmed" ta="center" py="md">No deals for this salesperson.</Text></Table.Td></Table.Tr>}
              {!sp && <Table.Tr><Table.Td colSpan={8}><Text c="dimmed" ta="center" py="md">Select a salesperson to see their deals.</Text></Table.Td></Table.Tr>}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </Paper>

      {sp && total > PAGE_SIZE && (
        <Group justify="flex-end">
          <Pagination size="sm" total={pageCount} value={page} onChange={setPage} />
        </Group>
      )}
    </Stack>
  );
}

function Kpi({ label, value }) {
  return (
    <Paper withBorder radius="md" p="sm">
      <Text size="xs" c="dimmed">{label}</Text>
      <Text fw={700} size="lg">{value}</Text>
    </Paper>
  );
}
